import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Upload, X, Loader2, ImageIcon } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LogoUploader = ({ value, onChange, label = 'Logo', dark = true }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      alert('Please upload a JPEG, PNG, WebP, or GIF image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('File too large. Maximum size is 5MB.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/upload/public-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onChange(res.data.url);
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const textColor = dark ? 'text-white' : 'text-foreground';
  const mutedColor = dark ? 'text-white/50' : 'text-muted-foreground';
  const borderColor = dark ? 'border-white/10' : 'border-border';
  const bgColor = dark ? 'bg-white/5' : 'bg-muted/50';
  const hoverBg = dark ? 'hover:bg-white/10' : 'hover:bg-muted';
  const dragBg = dark ? 'bg-white/10 border-cameroon-green' : 'bg-muted border-cameroon-green';

  return (
    <div className="space-y-2" data-testid="logo-uploader">
      <label className={`text-sm font-medium ${dark ? 'text-white/80' : ''}`}>{label}</label>

      {value ? (
        <div className={`flex items-center gap-3 p-3 rounded-lg border ${borderColor} ${bgColor}`}>
          <img
            src={value}
            alt="Logo preview"
            className="w-14 h-14 rounded-lg object-contain bg-white/10 p-1"
            onError={(e) => { e.target.src = ''; e.target.alt = 'Failed to load'; }}
          />
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium ${textColor} truncate`}>Logo uploaded</p>
            <p className={`text-xs ${mutedColor} truncate`}>{value.split('/').pop()}</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange('')}
            className={`${mutedColor} hover:text-red-400`}
            data-testid="logo-remove-btn"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <div
          className={`relative rounded-lg border-2 border-dashed p-6 text-center transition-all cursor-pointer
            ${dragOver ? dragBg : `${borderColor} ${hoverBg}`}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !uploading && inputRef.current?.click()}
          data-testid="logo-dropzone"
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />

          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className={`w-8 h-8 ${mutedColor} animate-spin`} />
              <p className={`text-sm ${mutedColor}`}>Uploading...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className={`w-12 h-12 rounded-full ${bgColor} flex items-center justify-center`}>
                <Upload className={`w-5 h-5 ${mutedColor}`} />
              </div>
              <div>
                <p className={`text-sm font-medium ${textColor}`}>
                  Click to upload or drag & drop
                </p>
                <p className={`text-xs ${mutedColor} mt-1`}>
                  JPEG, PNG, WebP or GIF (max 5MB)
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LogoUploader;
